import React from 'react'
import { DollarSign, TrendingUp, RefreshCw, CreditCard, CheckCircle } from 'lucide-react'
import { ResponsiveContainer, AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip } from 'recharts'

export default function InsightsTab({ 
  insightsData, 
  insightsLoading, 
  refreshInsights,
  forecastData,
  syncing,
  syncStripeData
}) {
  if (insightsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
      </div>
    )
  }

  if (!insightsData) {
    return (
      <div className="text-center py-12 text-zinc-400">
        <p>Unable to load financial insights.</p>
        <button onClick={refreshInsights} className="mt-4 text-green-500 hover:underline">Try again</button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Sync Status */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold mb-1">Financial Insights</h2>
            <p className="text-indigo-200">Comprehensive analytics from synced Stripe data</p>
          </div>
          <button
            onClick={syncStripeData}
            disabled={syncing}
            className="flex items-center gap-2 px-4 py-2 bg-zinc-800/20 rounded-lg hover:bg-zinc-800/30 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync Stripe'}
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-4 border border-emerald-200">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-5 h-5 text-emerald-400" />
            <span className="text-sm font-medium text-emerald-400">Net Revenue</span>
          </div>
          <p className="text-2xl font-bold text-emerald-400">
            ${insightsData.revenue?.net_revenue?.toLocaleString()}
          </p>
          <p className={`text-xs mt-1 ${insightsData.revenue?.growth_percent >= 0 ? 'text-green-600' : 'text-red-400'}`}>
            {insightsData.revenue?.growth_percent >= 0 ? '+' : ''}{insightsData.revenue?.growth_percent?.toFixed(1)}% vs previous
          </p>
        </div>

        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-4 border border-indigo-200">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-5 h-5 text-green-500" />
            <span className="text-sm font-medium text-green-400">MRR</span>
          </div>
          <p className="text-2xl font-bold text-green-500">
            ${insightsData.subscriptions?.mrr?.toLocaleString()}
          </p>
          <p className="text-xs text-zinc-400">ARR: ${insightsData.subscriptions?.arr?.toLocaleString()}</p>
        </div>

        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-4 border border-amber-200">
          <div className="flex items-center gap-2 mb-2">
            <CreditCard className="w-5 h-5 text-amber-600" />
            <span className="text-sm font-medium text-amber-400">Refund Rate</span>
          </div>
          <p className="text-2xl font-bold text-amber-600">
            {((insightsData.revenue?.refunds_total / (insightsData.revenue?.total || 1)) * 100).toFixed(1)}%
          </p>
          <p className="text-xs text-zinc-400">${insightsData.revenue?.refunds_total?.toLocaleString()} refunded</p>
        </div>

        <div className={`rounded-2xl p-4 ${insightsData.transactions?.success_rate > 95 ? 'bg-green-500/10 border border-green-500/30' : insightsData.transactions?.success_rate > 90 ? 'bg-yellow-500/10 border border-yellow-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="w-5 h-5 text-zinc-300" />
            <span className="text-sm font-medium text-zinc-200">Success Rate</span>
          </div>
          <p className={`text-2xl font-bold ${insightsData.transactions?.success_rate > 95 ? 'text-green-600' : insightsData.transactions?.success_rate > 90 ? 'text-yellow-400' : 'text-red-400'}`}>
            {insightsData.transactions?.success_rate?.toFixed(1)}%
          </p>
          <p className="text-xs text-zinc-400">{insightsData.transactions?.failed || 0} failed</p>
        </div>
      </div>

      {/* Revenue & Subscription Metrics */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-zinc-800 rounded-2xl p-6 border border-zinc-700">
          <h3 className="font-semibold text-white mb-4">Revenue Breakdown</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-zinc-300">Gross Revenue</span>
              <span className="font-bold text-white">${insightsData.revenue?.total?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-zinc-300">Refunds</span>
              <span className="font-bold text-red-400">-${insightsData.revenue?.refunds_total?.toLocaleString()}</span>
            </div>
            <div className="border-t pt-4 flex justify-between items-center">
              <span className="text-white font-medium">Net Revenue</span>
              <span className="font-bold text-green-600 text-lg">${insightsData.revenue?.net_revenue?.toLocaleString()}</span>
            </div>
          </div>
        </div>

        <div className="bg-zinc-800 rounded-2xl p-6 border border-zinc-700">
          <h3 className="font-semibold text-white mb-4">Subscription Metrics</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-zinc-300">Active Subscriptions</span>
              <span className="font-bold text-white">{insightsData.subscriptions?.active}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-zinc-300">MRR</span>
              <span className="font-bold text-green-500">${insightsData.subscriptions?.mrr?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-zinc-300">Est. LTV</span>
              <span className="font-bold text-green-500">${insightsData.customers?.estimated_ltv?.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Revenue Chart */}
      <div className="bg-zinc-800 rounded-2xl p-6 border border-zinc-700">
        <h3 className="font-semibold text-white mb-4">Revenue & Refunds Trend</h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={insightsData.revenue?.by_day || []}>
              <defs>
                <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="refundGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#EF4444" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#EF4444" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#3f3f46" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#a1a1aa' }} tickFormatter={(v) => v?.slice(5)} />
              <YAxis tick={{ fontSize: 11, fill: '#a1a1aa' }} tickFormatter={(v) => `$${v}`} />
              <Tooltip formatter={(v) => [`$${v?.toFixed(2)}`, '']} />
              <Area type="monotone" dataKey="revenue" name="Revenue" stroke="#10B981" strokeWidth={2} fill="url(#revenueGradient)" />
              <Area type="monotone" dataKey="refunds" name="Refunds" stroke="#EF4444" strokeWidth={2} fill="url(#refundGradient)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Forecast Section */}
      {forecastData && (
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-100">
          <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-500" />
            Revenue Forecast
          </h3>
          <div className="grid md:grid-cols-4 gap-4 mb-6">
            <div className="bg-zinc-800/70 rounded-xl p-4">
              <p className="text-sm text-zinc-300">Current MRR</p>
              <p className="text-2xl font-bold text-green-500">${forecastData.current_mrr?.toLocaleString()}</p>
            </div>
            <div className="bg-zinc-800/70 rounded-xl p-4">
              <p className="text-sm text-zinc-300">Current ARR</p>
              <p className="text-2xl font-bold text-green-500">${forecastData.current_arr?.toLocaleString()}</p>
            </div>
            <div className="bg-zinc-800/70 rounded-xl p-4">
              <p className="text-sm text-zinc-300">Avg Growth</p>
              <p className={`text-2xl font-bold ${forecastData.average_monthly_growth >= 0 ? 'text-green-600' : 'text-red-400'}`}>
                {forecastData.average_monthly_growth >= 0 ? '+' : ''}{forecastData.average_monthly_growth}%
              </p>
            </div>
            <div className="bg-zinc-800/70 rounded-xl p-4">
              <p className="text-sm text-zinc-300">Active Subs</p>
              <p className="text-2xl font-bold text-white">{forecastData.active_subscriptions}</p>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {forecastData.forecast?.map((f, i) => (
              <div key={i} className={`rounded-xl p-4 ${f.confidence === 'high' ? 'bg-green-500/20' : f.confidence === 'medium' ? 'bg-yellow-500/20' : 'bg-zinc-800'}`}>
                <p className="text-sm text-zinc-300">{f.month}</p>
                <p className="text-xl font-bold text-white">${f.projected_revenue?.toLocaleString()}</p>
                <p className={`text-xs ${f.confidence === 'high' ? 'text-green-600' : f.confidence === 'medium' ? 'text-yellow-400' : 'text-zinc-400'}`}>
                  {f.confidence} confidence
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Balance */}
      <div className="bg-zinc-800 rounded-2xl p-6 border border-zinc-700">
        <h3 className="font-semibold text-white mb-4">Stripe Balance</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="p-4 bg-green-500/10 rounded-xl">
            <p className="text-sm text-green-400">Available</p>
            <p className="text-2xl font-bold text-green-600">${insightsData.balance?.available?.toLocaleString()}</p>
          </div>
          <div className="p-4 bg-yellow-500/10 rounded-xl">
            <p className="text-sm text-yellow-400">Pending</p>
            <p className="text-2xl font-bold text-yellow-400">${insightsData.balance?.pending?.toLocaleString()}</p>
          </div>
          <div className="p-4 bg-zinc-900 rounded-xl">
            <p className="text-sm text-zinc-200">Total</p>
            <p className="text-2xl font-bold text-white">${((insightsData.balance?.available || 0) + (insightsData.balance?.pending || 0)).toLocaleString()}</p>
          </div>
        </div>
      </div>
    </div>
  )
}
